import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaPlus, FaCheck, FaClock, FaTrash, FaEdit, FaCalendarAlt,
  FaPaperclip, FaUpload, FaTimes, FaSave, FaFileAlt, FaTags
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import { remindersApi } from './api/remindersApi';
import { useTheme } from '../../core/theme/ThemeContext';
import './Reminders.css';

// Use remindersApi for structured calls
const remindersAPI = remindersApi;

// Helper function to get theme-aware colors
const getThemeColors = (theme) => {
  return theme === 'light' ? {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textMuted: '#64748b',
    bgPrimary: 'rgba(255, 255, 255, 0.9)',
    bgSecondary: 'rgba(255, 255, 255, 0.05)',
    bgHover: 'rgba(100, 116, 139, 0.08)',
    border: 'rgba(15, 23, 42, 0.1)',
    borderLight: 'rgba(15, 23, 42, 0.08)'
  } : {
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.8)',
    textMuted: 'rgba(255, 255, 255, 0.5)',
    bgPrimary: 'rgba(255, 255, 255, 0.05)',
    bgSecondary: 'rgba(255, 255, 255, 0.02)',
    bgHover: 'rgba(255, 255, 255, 0.03)',
    border: 'rgba(255, 255, 255, 0.1)',
    borderLight: 'rgba(255, 255, 255, 0.08)'
  };
};

// Komponent pre jednotlivú pripomienku
function ReminderItem({ reminder, index, toggleComplete, showReminderDetail, startEdit, deleteReminder, formatDate, calculateProgress, theme }) {
  const progress = calculateProgress(reminder.due_date);
  const colors = getThemeColors(theme);
  
  return (
    <motion.div
      className={`reminder-item compact ${reminder.completed ? 'completed' : ''}`}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      style={{
        padding: '10px',
        marginBottom: '8px',
        background: reminder.completed ? colors.bgSecondary : colors.bgPrimary,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <motion.button
          className="checkbox-btn"
          onClick={() => toggleComplete(reminder.id, reminder.completed)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{ marginTop: '2px' }}
        >
          {reminder.completed && <FaCheck />}
        </motion.button>

        <div style={{ flex: 1 }}>
          <div
            onClick={() => showReminderDetail(reminder)}
            style={{ cursor: 'pointer' }}
          >
            <h4 style={{
              margin: '0 0 5px 0',
              fontSize: '15px',
              textDecoration: reminder.completed ? 'line-through' : 'none',
              color: reminder.completed ? colors.textMuted : colors.textPrimary
            }}>
              {reminder.title}
              {reminder.visibility_type === 'shared' && <span title="Zdieľané" style={{fontSize: '14px', marginLeft: '8px'}}>👥</span>}
            </h4>

            {reminder.due_date && (
              <div style={{ fontSize: '13px', color: progress?.color || colors.textSecondary, marginBottom: '3px' }}>
                <FaCalendarAlt style={{ fontSize: '11px', marginRight: '5px' }} />
                {formatDate(reminder.due_date)}
                {progress && !reminder.completed && (
                  <span style={{ marginLeft: '8px' }}>
                    ({progress.daysRemaining} dní)
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
            <button
              onClick={() => startEdit(reminder)}
              className="mini-btn"
              style={{
                padding: '3px 8px',
                fontSize: '12px',
                background: 'rgba(33, 150, 243, 0.2)',
                border: 'none',
                borderRadius: '4px',
                color: '#2196F3',
                cursor: 'pointer'
              }}
            >
              <FaEdit />
            </button>
            <button
              onClick={() => deleteReminder(reminder.id)}
              className="mini-btn"
              style={{
                padding: '3px 8px',
                fontSize: '12px',
                background: 'rgba(244, 67, 54, 0.2)',
                border: 'none',
                borderRadius: '4px',
                color: '#f44336',
                cursor: 'pointer'
              }}
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Reminders() {
  const { theme } = useTheme();
  const colors = getThemeColors(theme);
  const [reminders, setReminders] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    visibility_type: 'private',
    shared_with_users: [],
    category_id: null
  });
  const [attachments, setAttachments] = useState([]);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileInputRef] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    color: '#4CAF50',
    icon: '📁'
  });
  const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'all'
  const [expandedCategories, setExpandedCategories] = useState({});
  const [sortBy, setSortBy] = useState('due_date'); // 'due_date', 'created', 'title'

  // Helper funkcia na formátovanie dátumu do DD.MM.YYYY formátu
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Konverzia na YYYY-MM-DD pre backend (vstup môže byť DD.MM.YYYY alebo už YYYY-MM-DD)
  const parseDate = (dateString) => {
    if (!dateString) return '';
    // Already YYYY-MM-DD from <input type="date">
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    const parts = dateString.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (parts) {
      const [, day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Konverzia na YYYY-MM-DD pre <input type="date">
  const dateToInput = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('T')) {
      dateString = dateString.split('T')[0];
    }
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    return dateString;
  };

  // Formátovanie inputu pri písaní
  const handleDateInput = (value) => {
    // Odstráň všetko okrem čísel a bodiek
    let cleaned = value.replace(/[^\d.]/g, '');
    
    // Automaticky pridaj bodky
    if (cleaned.length >= 2 && cleaned[2] !== '.') {
      cleaned = cleaned.slice(0, 2) + '.' + cleaned.slice(2);
    }
    if (cleaned.length >= 5 && cleaned[5] !== '.') {
      cleaned = cleaned.slice(0, 5) + '.' + cleaned.slice(5);
    }
    
    // Obmedz dĺžku
    if (cleaned.length > 10) {
      cleaned = cleaned.slice(0, 10);
    }
    
    return cleaned;
  };

  useEffect(() => {
    fetchReminders();
    fetchAvailableUsers();
    fetchCategories();
  }, []);

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/users/for-sharing');
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/reminders/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const createCategory = async () => {
    if (!newCategoryData.name) {
      toast.error('Názov kategórie je povinný');
      return;
    }

    try {
      const response = await api.post('/reminders/categories', newCategoryData);
      toast.success('Kategória vytvorená');
      fetchCategories();
      setShowNewCategory(false);
      setNewCategoryData({ name: '', color: '#4CAF50', icon: '📁' });
      setFormData({...formData, category_id: response.data.id});
    } catch (error) {
      toast.error('Chyba pri vytváraní kategórie');
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await remindersAPI.getReminders();
      setReminders(response.data);
      // Automaticky rozbaliť kategórie s pripomienkami
      const expanded = {};
      response.data.forEach(reminder => {
        if (reminder.category_id) {
          expanded[reminder.category_id] = true;
        }
      });
      expanded['uncategorized'] = true; // Pre pripomienky bez kategórie
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Chyba pri načítaní pripomienok');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getRemindersByCategory = () => {
    const grouped = {
      uncategorized: []
    };
    
    categories.forEach(cat => {
      grouped[cat.id] = [];
    });
    
    reminders.forEach(reminder => {
      if (reminder.category_id && grouped[reminder.category_id]) {
        grouped[reminder.category_id].push(reminder);
      } else {
        grouped.uncategorized.push(reminder);
      }
    });
    
    return grouped;
  };

  const getSortedReminders = (reminderList) => {
    return [...reminderList].sort((a, b) => {
      if (sortBy === 'due_date') {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      } else if (sortBy === 'created') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
  };

  const getAllReminders = () => {
    return getSortedReminders(reminders);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Konvertuj dátum z DD.MM.YYYY na YYYY-MM-DD pre backend
      const dataToSend = {
        ...formData,
        due_date: parseDate(formData.due_date)
      };
      
      let reminderId;
      if (editingId) {
        await remindersAPI.updateReminder(editingId, dataToSend);
        toast.success('Pripomienka aktualizovaná');
        reminderId = editingId;
        setEditingId(null);
      } else {
        const response = await remindersAPI.createReminder(dataToSend);
        toast.success('Pripomienka vytvorená');
        reminderId = response.data.id;
      }
      
      // Upload pending files
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file.file);
        formData.append('description', file.description);
        await remindersAPI.uploadAttachment(reminderId, formData);
      }
      
      setFormData({ title: '', description: '', due_date: '', visibility_type: 'private', shared_with_users: [], category_id: null });
      setPendingFiles([]);
      setShowAddForm(false);
      fetchReminders();
    } catch (error) {
      toast.error(editingId ? 'Chyba pri aktualizácii' : 'Chyba pri vytváraní pripomienky');
    }
  };

  const toggleComplete = async (id, completed) => {
    try {
      await remindersAPI.updateReminder(id, { completed: !completed });
      fetchReminders();
    } catch (error) {
      toast.error('Chyba pri aktualizácii pripomienky');
    }
  };

  const deleteReminder = async (id) => {
    if (!window.confirm('Naozaj chcete zmazať túto pripomienku?')) return;
    
    try {
      await remindersAPI.deleteReminder(id);
      toast.success('Pripomienka zmazaná');
      fetchReminders();
    } catch (error) {
      toast.error('Chyba pri mazaní pripomienky');
    }
  };

  const startEdit = (reminder) => {
    setEditingId(reminder.id);
    setFormData({
      title: reminder.title,
      description: reminder.description || '',
      due_date: reminder.due_date ? dateToInput(reminder.due_date) : '',
      visibility_type: reminder.visibility_type || 'private',
      shared_with_users: reminder.shared_with_users || [],
      category_id: reminder.category_id || null
    });
    setPendingFiles([]);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ title: '', description: '', due_date: '', visibility_type: 'private', shared_with_users: [], category_id: null });
    setPendingFiles([]);
    setShowAddForm(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      file,
      description: '',
      name: file.name
    }));
    setPendingFiles([...pendingFiles, ...newFiles]);
  };

  const updateFileDescription = (index, description) => {
    const updated = [...pendingFiles];
    updated[index].description = description;
    setPendingFiles(updated);
  };

  const removeFile = (index) => {
    setPendingFiles(pendingFiles.filter((_, i) => i !== index));
  };

  const showReminderDetail = (reminder) => {
    setSelectedReminder(reminder);
    setShowDetail(true);
  };

  const deleteAttachment = async (reminderId, attachmentId) => {
    if (!window.confirm('Naozaj chcete zmazať túto prílohu?')) return;
    
    try {
      await remindersAPI.deleteAttachment(reminderId, attachmentId);
      toast.success('Príloha zmazaná');
      fetchReminders();
      if (selectedReminder?.id === reminderId) {
        const updated = await remindersAPI.getReminders();
        const reminder = updated.data.find(r => r.id === reminderId);
        setSelectedReminder(reminder);
      }
    } catch (error) {
      toast.error('Chyba pri mazaní prílohy');
    }
  };

  const downloadAttachment = async (reminderId, attachmentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/reminders/${reminderId}/attachments/${attachmentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Chyba pri sťahovaní prílohy');
    }
  };

  const calculateProgress = (dueDate) => {
    if (!dueDate) return null;
    
    const now = new Date();
    const due = new Date(dueDate);
    const created = new Date(due);
    created.setFullYear(created.getFullYear() - 1);
    
    const totalTime = due - created;
    const elapsed = now - created;
    const remaining = due - now;
    const daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));
    
    let percentage = (elapsed / totalTime) * 100;
    percentage = Math.min(100, Math.max(0, percentage));
    
    let color = '#10b981'; // Green
    if (daysRemaining <= 30) {
      color = '#ef4444'; // Red
    } else if (daysRemaining <= 90) {
      color = '#f59e0b'; // Orange
    }
    
    return {
      percentage,
      color,
      daysRemaining
    };
  };

  return (
    <div className="reminders-page">
      <div className="page-header">
        <h1>Pripomienky</h1>
        <motion.button 
          className="btn btn-primary"
          onClick={() => {
            setShowAddForm(!showAddForm);
            if (showAddForm) cancelEdit();
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaPlus /> Nová pripomienka
        </motion.button>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div 
            className="add-form-container"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <form onSubmit={handleSubmit} className="reminder-form">
              <h3>{editingId ? 'Upraviť pripomienku' : 'Nová pripomienka'}</h3>
              <div className="form-grid">
                <div className="input-group">
                  <label>Názov</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Názov pripomienky"
                    required
                  />
                </div>
                <div className="input-group">
                  <label>Dátum termínu</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div className="input-group">
                <label>Kategória</label>
                {!showNewCategory ? (
                  <select
                    className="reminder-select"
                    value={formData.category_id || ''}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        setShowNewCategory(true);
                      } else {
                        setFormData({...formData, category_id: e.target.value ? parseInt(e.target.value) : null});
                      }
                    }}
                  >
                    <option value="">Bez kategórie</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                    <option value="new">➕ Nová kategória...</option>
                  </select>
                ) : (
                  <div className="new-category-panel">
                    <h4>Nová kategória</h4>
                    <input
                      type="text"
                      className="reminder-input"
                      placeholder="Názov kategórie"
                      value={newCategoryData.name}
                      onChange={(e) => setNewCategoryData({...newCategoryData, name: e.target.value})}
                    />
                    <div className="cat-row">
                      <div className="cat-col">
                        <label className="cat-sublabel">Ikona</label>
                        <div className="icon-picker">
                          {['📁', '📌', '🏠', '💼', '🎯', '⭐', '🔔', '📝'].map(icon => (
                            <button
                              key={icon}
                              type="button"
                              className={`icon-btn ${newCategoryData.icon === icon ? 'selected' : ''}`}
                              onClick={() => setNewCategoryData({...newCategoryData, icon})}
                            >
                              {icon}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="cat-col">
                        <label className="cat-sublabel">Farba</label>
                        <input
                          type="color"
                          className="color-picker"
                          value={newCategoryData.color}
                          onChange={(e) => setNewCategoryData({...newCategoryData, color: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="cat-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={createCategory}>
                        Vytvoriť
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowNewCategory(false);
                          setNewCategoryData({ name: '', color: '#4CAF50', icon: '📁' });
                        }}
                      >
                        Zrušiť
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="input-group">
                <label>Poznámka</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Voliteľná poznámka k pripomienke"
                  rows="3"
                />
              </div>
              
              <div className="input-group">
                <label>Viditeľnosť</label>
                <div className="visibility-toggle">
                  <label className={`vis-option ${formData.visibility_type === 'private' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      value="private"
                      checked={formData.visibility_type === 'private'}
                      onChange={(e) => setFormData({...formData, visibility_type: e.target.value, shared_with_users: []})}
                    />
                    <span className="vis-icon">🔒</span>
                    <span>Len pre mňa</span>
                  </label>
                  <label className={`vis-option ${formData.visibility_type === 'shared' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      value="shared"
                      checked={formData.visibility_type === 'shared'}
                      onChange={(e) => setFormData({...formData, visibility_type: e.target.value})}
                    />
                    <span className="vis-icon">👥</span>
                    <span>Zdieľané</span>
                  </label>
                </div>

                {formData.visibility_type === 'shared' && (
                  <div className="share-panel">
                    <label className="share-label">Zdieľať s</label>
                    <div className="share-users">
                      {availableUsers.map(user => (
                        <label key={user.id} className="share-user">
                          <input
                            type="checkbox"
                            checked={formData.shared_with_users.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({...formData, shared_with_users: [...formData.shared_with_users, user.id]});
                              } else {
                                setFormData({...formData, shared_with_users: formData.shared_with_users.filter(id => id !== user.id)});
                              }
                            }}
                          />
                          <span className="share-user-avatar">{user.username[0]?.toUpperCase()}</span>
                          <span className="share-user-name">{user.username}</span>
                        </label>
                      ))}
                    </div>
                    {availableUsers.length === 0 && (
                      <p className="share-empty">Žiadni užívatelia nie sú k dispozícii</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="input-group">
                <label>Prílohy</label>
                <input
                  type="file"
                  className="reminder-file-input"
                  multiple
                  onChange={handleFileSelect}
                />
                {pendingFiles.length > 0 && (
                  <div className="pending-files">
                    {pendingFiles.map((file, index) => (
                      <div key={index} className="pending-file-item">
                        <span>{file.name}</span>
                        <input
                          type="text"
                          placeholder="Popis prílohy"
                          value={file.description}
                          onChange={(e) => updateFileDescription(index, e.target.value)}
                          style={{ marginLeft: '10px', flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="btn-icon delete"
                          style={{ marginLeft: '10px' }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="form-buttons">
                <button type="submit" className="btn btn-primary">
                  {editingId ? <><FaSave /> Uložiť zmeny</> : 'Pridať pripomienku'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={cancelEdit}
                >
                  <FaTimes /> Zrušiť
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="view-controls" style={{ 
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        padding: '15px',
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className={`view-btn ${viewMode === 'categories' ? 'active' : ''}`}
            onClick={() => setViewMode('categories')}
            style={{
              padding: '8px 15px',
              borderRadius: '8px',
              border: 'none',
              background: viewMode === 'categories' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255, 255, 255, 0.08)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <FaTags /> Podľa kategórií
          </button>
          <button
            className={`view-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
            style={{
              padding: '8px 15px',
              borderRadius: '8px',
              border: 'none',
              background: viewMode === 'all' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255, 255, 255, 0.08)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <FaClock /> Všetky pripomienky
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontSize: '14px', color: '#ffffff' }}>Zoradiť:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '2px solid rgba(255, 255, 255, 0.15)',
              fontSize: '14px',
              background: 'rgba(15, 23, 42, 0.6)',
              color: '#ffffff'
            }}
          >
            <option value="due_date">Podľa termínu</option>
            <option value="title">Podľa názvu</option>
            <option value="created">Najnovšie</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : viewMode === 'categories' ? (
        <div className="categories-view">
          {reminders.length === 0 ? (
            <div className="empty-state">
              <FaClock className="empty-icon" />
              <h3>Žiadne pripomienky</h3>
              <p>Pridajte svoju prvú pripomienku</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {/* Nezaradené pripomienky */}
              {getRemindersByCategory().uncategorized.length > 0 && (
                <motion.div
                  className="category-box"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    background: colors.bgHover,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: '15px',
                    boxShadow: theme === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: `1px solid ${colors.borderLight}`
                  }}
                >
                  <div
                    className="category-header"
                    onClick={() => toggleCategory('uncategorized')}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: expandedCategories['uncategorized'] ? '15px' : '0',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '20px' }}>📋</span>
                      <h3 style={{ margin: 0, color: colors.textPrimary }}>Bez kategórie</h3>
                      <span style={{
                        background: theme === 'light' ? 'rgba(15, 23, 42, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        color: colors.textPrimary
                      }}>
                        {getRemindersByCategory().uncategorized.length}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: expandedCategories['uncategorized'] ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ color: colors.textPrimary }}
                    >
                      ▼
                    </motion.div>
                  </div>
                  <AnimatePresence>
                    {expandedCategories['uncategorized'] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {getSortedReminders(getRemindersByCategory().uncategorized).map((reminder, idx) => (
                          <ReminderItem
                            key={reminder.id}
                            reminder={reminder}
                            index={idx}
                            toggleComplete={toggleComplete}
                            showReminderDetail={showReminderDetail}
                            startEdit={startEdit}
                            deleteReminder={deleteReminder}
                            formatDate={formatDate}
                            calculateProgress={calculateProgress}
                            theme={theme}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}

              {/* Kategórie s pripomienkami */}
              {categories.map((category, catIndex) => {
                const categoryReminders = getRemindersByCategory()[category.id] || [];
                if (categoryReminders.length === 0) return null;
                
                return (
                  <motion.div
                    key={category.id}
                    className="category-box"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: catIndex * 0.1 }}
                    style={{
                      background: colors.bgHover,
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      padding: '15px',
                      boxShadow: theme === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                      border: `1px solid ${category.color}40`,
                      borderLeft: `5px solid ${category.color}`
                    }}
                  >
                    <div
                      className="category-header"
                      onClick={() => toggleCategory(category.id)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: expandedCategories[category.id] ? '15px' : '0',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{category.icon}</span>
                        <h3 style={{ margin: 0, color: category.color }}>{category.name}</h3>
                        <span style={{
                          background: `${category.color}20`,
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          color: category.color
                        }}>
                          {categoryReminders.length}
                        </span>
                      </div>
                      <motion.div
                        animate={{ rotate: expandedCategories[category.id] ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ color: category.color }}
                      >
                        ▼
                      </motion.div>
                    </div>
                    <AnimatePresence>
                      {expandedCategories[category.id] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {getSortedReminders(categoryReminders).map((reminder, idx) => (
                            <ReminderItem
                              key={reminder.id}
                              reminder={reminder}
                              index={idx}
                              toggleComplete={toggleComplete}
                              showReminderDetail={showReminderDetail}
                              startEdit={startEdit}
                              deleteReminder={deleteReminder}
                              formatDate={formatDate}
                              calculateProgress={calculateProgress}
                              theme={theme}
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="reminders-list">
          {reminders.length === 0 ? (
            <div className="empty-state">
              <FaClock className="empty-icon" />
              <h3>Žiadne pripomienky</h3>
              <p>Pridajte svoju prvú pripomienku</p>
            </div>
          ) : (
            getAllReminders().map((reminder, index) => {
              const progress = calculateProgress(reminder.due_date);
              
              return (
                <motion.div 
                  key={reminder.id}
                  className={`reminder-item ${reminder.completed ? 'completed' : ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="reminder-content">
                    <motion.button 
                      className="checkbox-btn"
                      onClick={() => toggleComplete(reminder.id, reminder.completed)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {reminder.completed && <FaCheck />}
                    </motion.button>
                    
                    <div className="reminder-info">
                      <h3 
                        className="reminder-title clickable"
                        onClick={() => showReminderDetail(reminder)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {reminder.title}
                        {reminder.visibility_type === 'shared' && <span title="Zdieľané" style={{fontSize: '16px'}}>👥</span>}
                        {!reminder.is_owner && <span title="Zdieľané so mnou" style={{fontSize: '14px', color: '#666'}}>↩️</span>}
                      </h3>
                      <div className="reminder-meta">
                        {reminder.category && (
                          <div className="reminder-category" style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '5px',
                            padding: '3px 10px',
                            background: reminder.category.color + '20',
                            border: `1px solid ${reminder.category.color}`,
                            borderRadius: '15px',
                            marginBottom: '8px'
                          }}>
                            <span style={{ fontSize: '14px' }}>{reminder.category.icon}</span>
                            <span style={{ fontSize: '13px', color: reminder.category.color, fontWeight: '500' }}>
                              {reminder.category.name}
                            </span>
                          </div>
                        )}
                        {reminder.due_date && (
                          <div className="reminder-date">
                            <FaCalendarAlt />
                            <span>{formatDate(reminder.due_date)}</span>
                            {progress && !reminder.completed && (
                              <span className="days-remaining" style={{ color: progress.color }}>
                                ({progress.daysRemaining} dní zostáva)
                              </span>
                            )}
                          </div>
                        )}
                        {reminder.description && (
                          <div className="reminder-description">
                            <FaFileAlt />
                            <span>{reminder.description}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="reminder-actions">
                      <motion.button 
                        className="action-btn edit-btn"
                        onClick={() => startEdit(reminder)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Upraviť"
                      >
                        <FaEdit />
                      </motion.button>
                      
                      
                      <motion.button 
                        className="action-btn delete-btn"
                        onClick={() => deleteReminder(reminder.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Zmazať"
                      >
                        <FaTrash />
                      </motion.button>
                    </div>
                  </div>
                  
                  {progress && !reminder.completed && (
                    <div className="progress-container">
                      <div className="progress-bar">
                        <motion.div 
                          className="progress-fill"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress.percentage}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          style={{ backgroundColor: progress.color }}
                        />
                      </div>
                    </div>
                  )}
                  
                </motion.div>
              );
            })
          )}
        </div>
      )}
      
      {/* Detail Modal */}
      <AnimatePresence>
        {showDetail && selectedReminder && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetail(false)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{selectedReminder.title}</h2>
                <button 
                  className="close-btn" 
                  onClick={() => setShowDetail(false)}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="modal-body">
                {selectedReminder.due_date && (
                  <div className="detail-item">
                    <FaCalendarAlt />
                    <span>Termín: {formatDate(selectedReminder.due_date)}</span>
                  </div>
                )}
                
                {selectedReminder.description && (
                  <div className="detail-item">
                    <FaFileAlt />
                    <span>Poznámka: {selectedReminder.description}</span>
                  </div>
                )}
                
                <div className="detail-section">
                  <h3>Prílohy</h3>
                  {selectedReminder.attachments && selectedReminder.attachments.length > 0 ? (
                    <div className="attachments-detail">
                      {selectedReminder.attachments.map(att => (
                        <div key={att.id} className="attachment-detail-item">
                          <div className="attachment-info">
                            <FaPaperclip />
                            <span 
                              className="attachment-link"
                              style={{ cursor: 'pointer' }}
                              onClick={() => downloadAttachment(selectedReminder.id, att.id, att.original_filename || att.filename)}
                            >
                              {att.original_filename || att.filename}
                            </span>
                            {att.description && (
                              <span className="attachment-desc">- {att.description}</span>
                            )}
                          </div>
                          <button
                            className="btn-icon delete"
                            onClick={() => deleteAttachment(selectedReminder.id, att.id)}
                            title="Zmazať prílohu"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-attachments">Žiadne prílohy</p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Reminders;